/**
 * Service Worker Registration Utility
 * 
 * This file contains utilities for registering and managing service workers.
 * The service worker implementation is in src/sw.ts and is managed by Vite PWA plugin.
 */

// Check if running in a browser environment
const isBrowser = typeof window !== 'undefined' && 'serviceWorker' in navigator;

// Simple logger for service worker events
const logger = {
  log: (...args: any[]) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Service Worker]', ...args);
    }
  },
  error: (...args: any[]) => {
    console.error('[Service Worker]', ...args);
  }
};

/**
 * Register the service worker
 * This is called by the application to ensure the service worker is properly registered
 * @returns A promise that resolves with the service worker registration or null if registration fails
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isBrowser || import.meta.env.DEV) {
    return null;
  }

  try {
    // Unregister any existing service workers first to avoid conflicts
    const unregistered = await unregisterAllServiceWorkers();
    if (!unregistered) {
      logger.error('Failed to unregister existing service workers');
      return null;
    }
    
    // Register the service worker
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      type: 'module'
    });
    
    logger.log('ServiceWorker registration successful with scope: ', registration.scope);
    
    // Initialize the service worker state
    if (window.__medulerServiceWorkerState) {
      window.__medulerServiceWorkerState.registrationAttempted = true;
      window.__medulerServiceWorkerState.updateAvailable = false;
    }
    
    // Set up event listeners for the service worker
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;
      
      newWorker.addEventListener('statechange', () => {
        switch (newWorker.state) {
          case 'installed':
            if (navigator.serviceWorker.controller) {
              // New update available
              logger.log('New service worker installed');
              if (window.__medulerServiceWorkerState) {
                window.__medulerServiceWorkerState.updateAvailable = true;
              }
            } else {
              // First installation
              logger.log('Service worker installed for the first time');
            }
            break;
          case 'activated':
            logger.log('Service worker activated');
            // Reload the page to use the new service worker
            window.location.reload();
            break;
        }
      });
    });
    
    // Check for updates on page load
    if (registration.waiting) {
      logger.log('Service worker waiting to activate');
      if (window.__medulerServiceWorkerState) {
        window.__medulerServiceWorkerState.updateAvailable = true;
      }
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    
    // Listen for updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            logger.log('New content is available; please refresh.');
            // Trigger a skipWaiting to activate the new service worker
            newWorker.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      }
    });
    
    // Check for updates every hour
    setInterval(() => {
      registration.update().catch(error => {
        logger.error('Error during service worker update:', error);
      });
    }, 60 * 60 * 1000);
    
    return registration;
  } catch (error) {
    logger.error('Service worker registration failed:', error);
    return null;
  }
}

function setupServiceWorkerMessaging() {
  navigator.serviceWorker.addEventListener('message', event => {
    if (event.data && event.data.type) {
      switch (event.data.type) {
        case 'PUID_RETRIEVED':
          break;
        case 'OFFLINE_EVENTS_PROCESSED':
          break;
        default:
          break;
      }
    }
  });

  window.addEventListener('online', () => {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'PROCESS_OFFLINE_EVENTS'
      });

      if ('serviceWorker' in navigator && 'SyncManager' in window) {
        navigator.serviceWorker.ready
          .then(registration => {
            return (registration as any).sync.register('sync-analytics');
          })
          .catch(() => {});
      }
    }
  });

  // Debug WebSocket connection attempts - This should be done in the service worker itself
  // rather than adding an event listener to the controller
  // The actual fetch event handling is now properly typed in the service worker scope below
}

export function storePUIDInServiceWorker(puid: string, encryptedPuid: string): void {
  if (navigator.serviceWorker && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'STORE_PUID',
      puid,
      encryptedPuid
    });
  }
}

export function queueAnalyticsInServiceWorker(
  eventName: string, 
  properties: Record<string, any>, 
  puid: string
): void {
  if (navigator.serviceWorker && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'QUEUE_ANALYTICS',
      eventName,
      properties,
      puid
    });
  }
}

/**
 * Unregisters all service workers and clears their caches
 * @returns A promise that resolves to true if all service workers were unregistered successfully, false otherwise
 */
export async function unregisterAllServiceWorkers(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    logger.log('Service worker unregistration skipped: Not supported in this browser');
    return false;
  }

  try {
    // Get all service worker registrations
    const registrations = await navigator.serviceWorker.getRegistrations();
    
    if (registrations.length === 0) {
      logger.log('No service worker registrations found to unregister');
      return true;
    }
    
    logger.log(`Unregistering ${registrations.length} service worker(s)`);
    
    // Unregister all service workers
    await Promise.all(registrations.map(registration => {
      logger.log(`Unregistering service worker: ${registration.scope}`);
      return registration.unregister();
    }));
    
    // Clear all caches
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => {
            logger.log(`Deleting cache: ${cacheName}`);
            return caches.delete(cacheName);
          })
        );
      } catch (cacheError) {
        logger.error('Error clearing caches:', cacheError);
        // Continue even if cache clearing fails
      }
    }
    
    logger.log('All service workers unregistered and caches cleared');
    return true;
  } catch (error) {
    logger.error('Error unregistering service workers:', error);
    return false;
  }
}

const registerSW = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      await navigator.serviceWorker.ready;
      try {
      } catch (error) {
      }
    });
  }
};

// TypeScript definitions for service worker events
declare const self: ServiceWorkerGlobalScope;

// Extend the global scope with additional types
interface ExtendableEvent extends Event {
  waitUntil(promise: Promise<any>): void;
}

// Define the Client type if not available
type Client = {
  id: string;
  type: 'window' | 'worker' | 'sharedworker' | 'all';
  url: string;
  frameType: 'auxiliary' | 'top-level' | 'nested' | 'none';
  postMessage(message: any, transfer?: Transferable[]): void;
};

interface ExtendableMessageEvent extends ExtendableEvent {
  data: any;
  source?: Client | ServiceWorker | MessagePort | null;
  lastEventId?: string;
  origin: string;
  ports: ReadonlyArray<MessagePort>;
}

// Extend the global scope to include fetch event types
interface FetchEvent extends ExtendableEvent {
  readonly clientId: string;
  readonly preloadResponse: Promise<any>;
  readonly replacesClientId: string;
  readonly resultingClientId: string;
  readonly request: Request;
  respondWith(r: Response | Promise<Response>): void;
}

// Listen for the install event, which is fired when the service worker is first installed.
self.addEventListener('install', (event: Event) => {
  const extendableEvent = event as ExtendableEvent;
  
  // Skip waiting to activate the new service worker immediately
  extendableEvent.waitUntil(
    (async () => {
      await self.skipWaiting();
    })()
  );
});

// Listen for the activate event, which is fired after installation.
self.addEventListener('activate', (event: Event) => {
  const extendableEvent = event as ExtendableEvent;
  
  extendableEvent.waitUntil(
    (async () => {
      // Claim all clients to take control of them immediately
      await self.clients.claim();
    })()
  );
});

// Listen for fetch events
self.addEventListener('fetch', (event: Event) => {
  // Type assertion for the fetch event
  const fetchEvent = event as unknown as FetchEvent;
  const request = fetchEvent.request;

  // Skip non-GET requests and non-http(s) requests
  if (request.method !== 'GET' || !request.url.startsWith('http')) {
    return;
  }

  // Debug WebSocket connection attempts
  if (request.url.startsWith('ws://') || request.url.startsWith('wss://')) {
    console.log('WebSocket connection attempt:', {
      url: request.url,
      mode: request.mode,
      credentials: request.credentials
    });
    return;
  }

  // Example of how to handle fetch events
  // fetchEvent.respondWith(
  //   caches.match(request).then(response => {
  //     return response || fetch(request);
  //   })
  // );
});

// Listen for messages from the main thread
self.addEventListener('message', (event: Event) => {
  const messageEvent = event as ExtendableMessageEvent;
  
  if (messageEvent.data && messageEvent.data.type === 'SKIP_WAITING') {
    self.skipWaiting();
  }
});